import styled from '@emotion/styled';
import { EventTypes } from '@racemap/sdk/schema/events';
import { RacemapColors } from '@racemap/utilities/consts/common';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import {
  Collapse as ACollapse,
  Button,
  type CollapseProps,
  Flex,
  Modal,
  Radio,
  Space,
  Typography,
} from 'antd';
import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getEventTypeDocuLink } from 'src/lib/EventUtils';
import { useIsMobile } from 'src/lib/customHooks';
import { DocuLink } from '../BasicComponents';
import { IconContestGroup, IconPlus, IconRegular, IconSafemap, IconStageGroup } from '../Icon';

const { Text, Paragraph } = Typography;

interface Props {
  buttonType?: 'primary' | 'default' | 'ghost';
  buttonSize?: 'large' | 'middle' | 'small';
  compact?: boolean;
}

export const CreateMapButton: FC<Props> = ({
  buttonType = 'primary',
  compact,
  buttonSize = 'large',
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<EventTypes | undefined>(
    EventTypes.REGULAR,
  );
  const navigate = useNavigate();
  const finalButtonSize = buttonSize || (isMobile ? 'middle' : 'large');
  const finalButtonType = buttonType === 'ghost' ? 'primary' : buttonType;
  const isGhost = buttonType === 'ghost';

  useEffect(() => {
    if (open) {
      setSelectedEventType(EventTypes.REGULAR);
    }
  }, [open]);

  const handleCreateClick = () => {
    switch (selectedEventType) {
      case EventTypes.REGULAR:
        navigate('/admin/events/new');
        break;
      case EventTypes.CONTEST_GROUP:
        navigate('/admin/contestgroups/new');
        break;
      case EventTypes.STAGE_GROUP:
        navigate('/admin/stagegroups/new');
        break;
      case EventTypes.SAFEMAP:
        navigate('/admin/safemaps/new');
        break;
      default:
        throw new Error('Unknown event type');
    }
    setOpen(false);
  };

  return (
    <>
      <Button
        type={finalButtonType}
        ghost={isGhost}
        icon={<IconPlus />}
        size={finalButtonSize}
        onClick={() => setOpen(true)}
        id={TestSelectors.admin.nav.buttons.newEvent.toString()}
      >
        {compact ? 'Map' : 'Create Map'}
      </Button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Create New Tracking Map"
        okText="Create"
        onOk={handleCreateClick}
        okButtonProps={{
          icon: <IconPlus />,
          disabled: !selectedEventType,
          id: TestSelectors.admin.nav.buttons.newEventModal.toString(),
        }}
      >
        <Space
          direction="vertical"
          size="middle"
          style={{ width: '100%' }}
          id={TestSelectors.admin.nav.modals.createNewEventModal.toString()}
        >
          <Text>
            Select the map type that best fits your event’s needs. You can create multiple maps, one
            after another. Each map can be configured and tested before activation.
          </Text>
          <Collapse
            accordion
            defaultActiveKey={EventTypes.REGULAR}
            items={items}
            expandIcon={({ isActive }) => <Radio checked={isActive} />}
            activeKey={selectedEventType}
            onChange={([key]) => setSelectedEventType(key as EventTypes)}
          />
        </Space>
      </Modal>
    </>
  );
};

const Collapse = styled(ACollapse)`
    .ant-collapse-header {
        align-items: center !important;
    }
`;

const Label = ({ text, icon }: { text: string; icon: React.ReactNode }) => (
  <Flex justify="space-between" align="center">
    <Text strong>{text}</Text>
    <IconWrapper>{icon}</IconWrapper>
  </Flex>
);

const items: CollapseProps['items'] = [
  {
    key: EventTypes.REGULAR,
    label: <Label text="Single Map" icon={<IconRegular />} />,
    children: (
      <Paragraph>
        Create and edit courses, add points of interest, manage participants. We recommend creating
        one map per race course—you can group them later if needed. Enhance your maps with add-ons
        like the leaderboard or API for additional features. All about customizing a single map in
        our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.REGULAR)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Paragraph>
    ),
  },
  {
    key: EventTypes.SAFEMAP,
    label: <Label text="Safemap" icon={<IconSafemap />} />,
    children: (
      <Paragraph>
        All critical safety information in one dashboard at a glance and in real-time. Receive
        alerts for SOS, crashes, off-track, or inactivity. Accessible only with a RACEMAP account
        and designed for the responsible persons for safety management - not for public display. All
        about setting up a safemap map in our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.SAFEMAP)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Paragraph>
    ),
  },
  {
    key: EventTypes.CONTEST_GROUP,
    label: <Label text="Group Map" icon={<IconContestGroup />} />,
    children: (
      <Paragraph>
        Combine multiple Single Maps - Marathon, Half Marathon, and 10K — under one URL with filters
        to quickly navigate each course. More information about Group Map in our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.CONTEST_GROUP)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Paragraph>
    ),
  },
  {
    key: EventTypes.STAGE_GROUP,
    label: <Label text="Stages" icon={<IconStageGroup />} />,
    children: (
      <Paragraph>
        Unlike a Group Map, participants are tracked through all stages of an event sequentially.
        More information about Stages in our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.STAGE_GROUP)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Paragraph>
    ),
  },
];

const IconWrapper = styled.div`
  font-size: 1.4em;
  color: ${RacemapColors.PaleBlue}
`;
